import React, { useState } from 'react';
import { BookOpen } from 'lucide-react';
import { cn } from '../ui/utils';

/** Category colours are stored as 6-digit hex (CourseCategory.colour); anything else is treated as unset. */
const HEX_COLOUR = /^#[0-9a-f]{6}$/i;

interface CourseThumbProps {
    /** Signed (or canonical) thumbnail URL. Absent, or failing to load, falls back to the tint. */
    thumbnailUrl?: string | null;
    categoryColour?: string | null;
    iconSize?: number;
}

/**
 * The tinted header block of a course card: the course thumbnail when there is one, otherwise
 * the category colour with a book glyph (LEARNING.md §7.4).
 *
 * Absolutely positioned so the card's own pill overlays (category, duration, status) keep
 * working unchanged — the parent supplies a `relative` flex box and the pills; this fills it.
 *
 * An `onError` fallback matters because thumbnails are served from a private bucket with a
 * signed, expiring URL: if the signature lapses while a page is open the image 403s, and the
 * colour block is a better outcome than a broken-image icon.
 */
export const CourseThumb: React.FC<CourseThumbProps> = ({
    thumbnailUrl,
    categoryColour,
    iconSize = 30,
}) => {
    const [failed, setFailed] = useState(false);
    const accent = categoryColour && HEX_COLOUR.test(categoryColour) ? categoryColour : null;
    const showImage = Boolean(thumbnailUrl) && !failed;

    return (
        <>
            <span
                aria-hidden
                className={cn('absolute inset-0', !showImage && !accent && 'bg-muted')}
                style={!showImage && accent ? { backgroundColor: `${accent}1f` } : undefined}
            />
            {showImage ? (
                <img
                    src={thumbnailUrl as string}
                    alt=""
                    loading="lazy"
                    onError={() => setFailed(true)}
                    className="absolute inset-0 h-full w-full object-cover"
                />
            ) : (
                <BookOpen
                    size={iconSize}
                    strokeWidth={1.4}
                    className="relative opacity-80"
                    style={{ color: accent ?? '#717182' }}
                />
            )}
        </>
    );
};

export default CourseThumb;
